import { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CIcon from '@coreui/icons-react';
import { CButton, CCard, CCardBody, CCol, CForm, CRow, CFormInput } from '@coreui/react';
import PropTypes from 'prop-types';
import CLabel from '../../../../../../../migration/CLabel';
import { Formik } from 'formik';
import { setFlexibleMultiModal } from '../../../../../../../../actions/common';
import { useDispatch } from 'react-redux';
import { COMPONENT_NAME } from '../../../../../../../../constants';
import { toast } from 'react-toastify';
import CTooltip from '../../../../../../../migration/CTooltip';
import { ConfirmSaveChange } from '../../../../../../../general/popup';
import Waiting from '../../../../../../../general/Loadings/Waiting';
import ImportExportTable from './ImportExportTable';
import CPagination from '../../../../../../../migration/pagination/CPagination';

const EditableCell = ({ value, onBlur, name, onRemove, isHover, disabled, dragHandleProps, isDrag }) => {
     const [inputValue, setInputValue] = useState(value);

     useEffect(() => {
          setInputValue(value);
     }, [value]);

     const handleBlur = (e) => {
          if (onBlur) {
               onBlur(e);
          }
     };

     const handleChange = (e) => {
          const newValue = e.target.value;
          setInputValue(newValue);
          onBlur({
               target: {
                    name: name,
                    value: newValue,
               },
          });
     };
     return (
          <td style={{ position: 'relative' }} className={isDrag ? 'event-variable-value event-variable-value-ellipsis' : ''}>
               <div style={{ display: 'flex', alignItems: 'center' }}>
                    {isDrag && (
                         <span {...dragHandleProps} style={{ marginRight: '10px', cursor: 'grab' }}>
                              <i className="fas fa-ellipsis-v"></i>
                         </span>
                    )}

                    <CFormInput
                         type="text"
                         name={name}
                         value={inputValue}
                         onChange={handleChange}
                         onBlur={handleBlur}
                         style={{ flexGrow: 1 }}
                         disabled={disabled}
                    />
               </div>
               {isHover && (
                    <div
                         className="row-actions"
                         style={{
                              position: 'absolute',
                              top: '50%',
                              right: '10px',
                              transform: 'translateY(-50%)',
                         }}
                    >
                         <span className="btn-remove-row">
                              <CTooltip content="Remove row" placement="top">
                                   <i className="fal fa-times fa-lg text-danger" onClick={onRemove}></i>
                              </CTooltip>
                         </span>
                    </div>
               )}
          </td>
     );
};

const DataSetValues = ({ attributes }) => {
     const dispatch = useDispatch();
     const { stepsData, setStepsData, onAccept, handleHasChange } = attributes;
     const [isRevert, setIsRevert] = useState(false);
     const [showConfirm, setShowConfirm] = useState(false);
     const [isSubmitting, setIsSubmitting] = useState(false);

     const [currentPage, setCurrentPage] = useState(1);
     const pageSize = 100;

     useEffect(() => {
          setIsRevert(stepsData.oldData?.length > 0);
     }, [stepsData.oldData]);

     const initialValues = {
          rules: stepsData.data || [{ description: '', classification: '' }],
          enableDescription: stepsData.enableDescription || false,
     };

     const validateRules = (rules) => {
          const seen = new Set();
          for (let i = 0; i < rules.length; i++) {
               const cls = rules[i].classification?.trim();
               if (!cls) {
                    toast.error(`Classification is required.`);
                    return false;
               }
               if (seen.has(cls.toLowerCase())) {
                    toast.error(`Classification "${cls}" is duplicated.`);
                    return false;
               }
               seen.add(cls.toLowerCase());
          }
          return true;
     };

     const onSubmit = async (values, { setSubmitting }) => {
          const newStepsData = { ...stepsData, data: values.rules };
          setStepsData(newStepsData);

          try {
               setIsSubmitting(true);
               await onAccept(newStepsData);
          } catch (error) {
               console.error('Submission Error:', error);
               toast.error('Failed to save changes.');
          } finally {
               setSubmitting(false);
               setShowConfirm(false);
               setIsSubmitting(false);
               dispatch(
                    setFlexibleMultiModal({
                         show: false,
                         component: COMPONENT_NAME.AI_INTENT_DATASET_VALUES,
                    })
               );
               handleHasChange(false);
          }
     };

     const saveChanges = (values) => {
          if (!validateRules(values.rules)) {
               return;
          }

          setShowConfirm(true);
     };

     return (
          <>
               <Formik initialValues={initialValues} validateOnChange={false} validateOnBlur={false} onSubmit={onSubmit}>
                    {({ values, setFieldValue, handleBlur, handleSubmit }) => {
                         const totalItems = values.rules.length;
                         const totalPages = Math.ceil(totalItems / pageSize);
                         const startIndex = (currentPage - 1) * pageSize;
                         const endIndex = startIndex + pageSize;
                         const currentItems = values.rules.slice(startIndex, endIndex);
                         const handleCellBlur = (e) => {
                              const { name, value } = e.target;
                              setFieldValue(name, value);
                              handleBlur(e);
                              handleHasChange(true);
                         };

                         const handleAddRow = (numberOfRows = 1) => {
                              const newRows = [];
                              for (let i = 0; i < numberOfRows; i++) {
                                   newRows.push({
                                        classification: '',
                                        description: '',
                                   });
                              }
                              const insertIndex =
                                   startIndex + (currentItems.length === pageSize ? currentItems.length - numberOfRows : currentItems.length);

                              const newRules = [...values.rules];
                              newRules.splice(insertIndex, 0, ...newRows);

                              setFieldValue('rules', newRules);
                              handleHasChange(true);
                         };

                         const handleRemoveRow = (indexToRemove) => {
                              let newRules = values.rules.filter((_, index) => index !== indexToRemove);
                              if (newRules.length === 0) {
                                   newRules = [
                                        {
                                             classification: '',
                                             description: '',
                                        },
                                   ];
                              }
                              setFieldValue('rules', newRules);
                              handleHasChange(true);
                         };

                         const setRevertModal = () => {
                              let revertRules = stepsData.oldData;
                              if (Array.isArray(revertRules) && Array.isArray(revertRules[0])) {
                                   revertRules = revertRules[0];
                              }
                              setFieldValue('rules', revertRules || []);
                              setIsRevert(false);
                         };

                         const handleOnDragEnd = (result) => {
                              if (!result.destination) {
                                   return;
                              }

                              const startIndex = result.source.index;
                              const endIndex = result.destination.index;

                              const newRules = Array.from(values.rules);
                              const [reorderedItem] = newRules.splice(startIndex, 1);
                              newRules.splice(endIndex, 0, reorderedItem);

                              setFieldValue('rules', newRules);
                              handleHasChange(true);
                         };

                         return (
                              <CForm className="form-edit-lookup" onSubmit={handleSubmit}>
                                   <CRow>
                                        <CCol md="12" className="form-edit-lookup-row">
                                             <div className="form-edit-lookup-title trigger-rule">
                                                  <h1 title={stepsData?.name}>{`Edit Data Set Values`}</h1>
                                             </div>

                                             <div className="form-edit-lookup-button text-right">
                                                  <div>
                                                       {isRevert && (
                                                            <CButton
                                                                 color="primary"
                                                                 onClick={setRevertModal}
                                                                 className="mr-3 d-inline-block text-uppercase"
                                                            >
                                                                 Revert
                                                            </CButton>
                                                       )}
                                                       <CButton
                                                            color="success"
                                                            type="button"
                                                            className="d-inline-block text-uppercase"
                                                            onClick={() => saveChanges(values)}
                                                       >
                                                            Save Changes
                                                       </CButton>
                                                  </div>
                                             </div>
                                        </CCol>
                                        <CCol md="12" className="form-edit-lookup-description">
                                             <p>
                                                  Manage your classification options in the following table. These options will be available to our AI
                                                  models when running your intent rules.
                                             </p>
                                        </CCol>
                                   </CRow>

                                   {values.rules?.length > 0 && (
                                        <CCard>
                                             <CCardBody>
                                                  <DragDropContext onDragEnd={handleOnDragEnd}>
                                                       <table className={`custom-table lookup-table ${values.enableDescription ? '' : 'single-column'}`}>
                                                            <thead>
                                                                 <tr>
                                                                      <th>Classification</th>
                                                                      {values.enableDescription && <th>Description</th>}
                                                                 </tr>
                                                            </thead>
                                                            <Droppable droppableId="rules">
                                                                 {(provided, snapshot) => (
                                                                      <tbody
                                                                           ref={provided.innerRef}
                                                                           {...provided.droppableProps}
                                                                           style={{
                                                                                background: snapshot.isDraggingOver ? '#f0f0f0' : 'white',
                                                                           }}
                                                                      >
                                                                           {currentItems.map((item, index) => (
                                                                                <Draggable
                                                                                     key={`row-${index + startIndex}`}
                                                                                     draggableId={`row-${index + startIndex}`}
                                                                                     index={index + startIndex}
                                                                                >
                                                                                     {(provided, snapshot) => (
                                                                                          <tr
                                                                                               className={`table-body ${
                                                                                                    snapshot.isDragging ? 'isDragging' : ''
                                                                                               }`}
                                                                                               ref={provided.innerRef}
                                                                                               {...provided.draggableProps}
                                                                                          >
                                                                                               <EditableCell
                                                                                                    value={item.classification}
                                                                                                    name={`rules[${
                                                                                                         index + startIndex
                                                                                                    }].classification`}
                                                                                                    onBlur={handleCellBlur}
                                                                                                    dragHandleProps={provided.dragHandleProps}
                                                                                                    isDrag={true}
                                                                                               />

                                                                                               {values.enableDescription && (
                                                                                                    <EditableCell
                                                                                                         value={item.description}
                                                                                                         name={`rules[${
                                                                                                              index + startIndex
                                                                                                         }].description`}
                                                                                                         onBlur={handleCellBlur}
                                                                                                         onRemove={() =>
                                                                                                              handleRemoveRow(index + startIndex)
                                                                                                         }
                                                                                                         isHover={true}
                                                                                                    />
                                                                                               )}
                                                                                          </tr>
                                                                                     )}
                                                                                </Draggable>
                                                                           ))}
                                                                           {provided.placeholder}
                                                                      </tbody>
                                                                 )}
                                                            </Droppable>
                                                       </table>
                                                  </DragDropContext>
                                             </CCardBody>
                                        </CCard>
                                   )}

                                   <div className="d-flex justify-content-center mt-3">
                                        <CPagination
                                             activePage={currentPage}
                                             pages={totalPages}
                                             onActivePageChange={setCurrentPage}
                                             align="center"
                                             className={'ai-insights-pagination'}
                                        />
                                   </div>

                                   <div className={`add-margin-cheat-sheet form-edit-lookup-row`}>
                                        <div className={`add-row-button mb-4 d-flex align-items-center `}>
                                             <div className="add-row-button-child d-flex">
                                                  <CButton className="btn-add-row" onClick={() => handleAddRow(1)}>
                                                       <CIcon icon="iconAddField" className="icon-add" />
                                                       <CLabel className="add-row mb-0">Add Row</CLabel>
                                                  </CButton>
                                                  <CButton className="btn-add-row" onClick={() => handleAddRow(5)}>
                                                       <CIcon icon="iconAddField" className="icon-add" />
                                                       <CLabel className="add-row mb-0">Add 5 Rows</CLabel>
                                                  </CButton>
                                             </div>
                                             <ImportExportTable
                                                  tableRow={values.rules}
                                                  setTableRow={(rows) => {
                                                       setFieldValue('rules', rows);
                                                  }}
                                             />
                                        </div>

                                        <div className="mb-3 form-edit-lookup-button text-right d-flex justify-content-end">
                                             <div>
                                                  {isRevert && (
                                                       <CButton
                                                            color="primary"
                                                            onClick={setRevertModal}
                                                            className="mr-3 d-inline-block text-uppercase"
                                                       >
                                                            Revert
                                                       </CButton>
                                                  )}
                                                  <CButton
                                                       color="success"
                                                       type="button"
                                                       className="d-inline-block text-uppercase"
                                                       onClick={() => saveChanges(values)}
                                                  >
                                                       <Waiting isLoading={isSubmitting}>Save Changes</Waiting>
                                                  </CButton>
                                             </div>
                                        </div>

                                        {showConfirm && (
                                             <ConfirmSaveChange
                                                  show={showConfirm}
                                                  onAccept={handleSubmit}
                                                  onClose={() => {
                                                       setShowConfirm(false);
                                                  }}
                                                  isLoading={isSubmitting}
                                                  title="Save Your Changes?"
                                             >
                                                  <p>
                                                       It looks like you have made changes to this lookup table but have not saved them. Would you
                                                       like to save the changes before navigating away?
                                                  </p>
                                             </ConfirmSaveChange>
                                        )}
                                   </div>
                              </CForm>
                         );
                    }}
               </Formik>
          </>
     );
};

DataSetValues.propTypes = {
     attributes: PropTypes.object,
};
export default DataSetValues;
